import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { CancelLineRequest, OrderCancelType, ReceivedGoods } from '@/infrastructure/FulfillmentAdminClient';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { LockIcon } from '@/pages/fulfillment/components/UltimateIcons';
import {
  cancelGoodsAnswered,
  cancelGoodsDispositions,
  type CancelGoodsDisposition,
  describeCancelGoods,
  type CancelGoodsChoice,
  type CancelGoodsLine,
} from '@/utils/cancelGoods';
import { describeCancelLock, type CancelLock } from '@/utils/fulfillmentCancelLock';
import type { CancelRequest as FiledCancelRequest } from '@/utils/cancelRequestHold';
import './cancelDialog.css';

/** What CS filed, shown read-only on FC's confirm, with who filed it. */
export interface CancelRequestPreset extends FiledCancelRequest {
  requestedBy?: string;
}

interface CancelDialogRequest {
  title: string;
  confirmLabel: string;
  maxReasonLength: number;
  /** Offer keep / write-off; only an order or line with received goods that can be written off has the choice. */
  showReceivedGoods?: boolean;
  /** Ask the cancel reason, whose group is the cancel type (SSD-7450). */
  askCancelType?: boolean;
  /** The order's cancel lock: shown, and on a locked order the dialog asks why it is cancelled anyway (SSD-7643). */
  lock?: CancelLock | null;
  /** The lines the cancel cancels, to say or ask what happens to each one's goods (SSD-7643); none skips the block. */
  goodsLines?: readonly CancelGoodsLine[];
  /** One sentence above the form on what this cancel will do beyond the usual (SSD-7676). */
  notice?: string;
  /** A box the operator must tick before the cancel goes, for a step with a physical side (SSD-7676). */
  confirmCheck?: string;
  /** A smaller second line under that box. */
  confirmHint?: string;
  /**
   * The reason someone already chose (SSD-7676 two-step cancel): CS asked, FC confirms. The reason and note boxes give way
   * to the request as written, and the answer carries it unchanged, so FC never re-types or re-picks CS's reason.
   */
  preset?: CancelRequestPreset;
}

/** What the dialog answers: the reason, the stock choice when it was offered, and the cancel type when it was asked. */
export interface CancelAnswer extends CancelLineRequest {
  cancelType?: OrderCancelType;
  /** Why a locked order is cancelled anyway (SSD-7643); set only on a locked order. The server posts it as a comment. */
  lockOverrideReason?: string;
  /** What to do with each line's goods (SSD-7643); set when any line needs action. The server posts it as a comment. */
  goodsNote?: string;
  /** The same choices by line, for the outbound cancel that books them (SSD-7676); set when any bought line was asked. */
  goodsChoices?: Partial<Record<number, CancelGoodsChoice>>;
  /** Structured per-line disposition; its stock effect is owned by CancelGoodsDisposition. */
  lines?: CancelGoodsDisposition[];
}

interface Pending extends CancelDialogRequest {
  id: number;
  settle: (answer: CancelAnswer | null) => void;
}

interface CancelReason {
  /** What the operator picks and what the event's reason starts with. */
  label: string;
  /** Write-ups that only name "other" need the memo to say what it was. */
  needsMemo?: boolean;
}

/**
 * The reasons CS cancels a web order for, from a year of refund notes and a sample of Gorgias cancel tickets
 * (SSD-7450). The group decides the cancel type: a customer's own reason (a change of mind) or ours.
 * Each reason is broad on purpose and the memo carries the detail, so the list stays short enough to read at a glance.
 * A customer who needs it sooner stays on their side; only our missed dispatch date is ours.
 */
const CANCEL_REASON_GROUPS: ReadonlyArray<{ type: OrderCancelType; label: string; reasons: readonly CancelReason[] }> =
  [
    {
      type: 'CHANGE_OF_MIND',
      label: "Customer's own reason",
      reasons: [
        { label: 'Changed their mind or gave no reason' },
        { label: 'Ordered by mistake: wrong item, shade, size, or twice' },
        { label: 'Will reorder: change items, address, coupon or credit' },
        { label: 'Needs it sooner, or worried about shipping cost or customs' },
        { label: 'Other customer reason', needsMemo: true },
      ],
    },
    {
      type: 'COMPANY',
      label: 'Our side',
      reasons: [
        { label: 'Out of stock, partly out of stock, or product changed' },
        { label: 'Cannot ship, or no reply to our question' },
        { label: 'We missed our promised dispatch date' },
        { label: 'Our error: price, coupon, website or duplicate payment' },
        { label: 'Suspected fraud or test order' },
        { label: 'Other reason on our side', needsMemo: true },
      ],
    },
  ];

/** Each reason keyed by group and position, so the select's value stays short and stable. */
const CANCEL_REASONS = new Map<string, CancelReason & { type: OrderCancelType }>(
  CANCEL_REASON_GROUPS.flatMap((group, g) =>
    group.reasons.map((reason, r) => [`${g}-${r}`, { ...reason, type: group.type }] as const),
  ),
);

const LONGEST_REASON = Math.max(...[...CANCEL_REASONS.values()].map((reason) => reason.label.length));
const MEMO_JOIN = ' - ';

/**
 * A locked order cancels only with a written reason (SSD-7643), whatever the cancel reason picked: the server posts it to
 * the order's comments, and refuses a change of mind after the lock without it. Any reason that is not blank will do;
 * the server caps it at 300. The dialog asks it in the one Note box, so the operator writes why once (SSD-7676).
 */
const OVERRIDE_MAX = 300;

const GOODS_CHOICES: readonly CancelGoodsChoice[] = ['RETURN', 'KEEP'];
const GOODS_CHOICES_UNARRIVED: readonly CancelGoodsChoice[] = ['RETURN', 'KEEP', 'NOT_SHIPPED'];

const CancelModal = ({
  title,
  confirmLabel,
  maxReasonLength,
  showReceivedGoods = true,
  askCancelType = false,
  lock = null,
  goodsLines = [],
  notice,
  confirmCheck,
  confirmHint,
  preset,
  settle,
}: Pending) => {
  const [checked, setChecked] = useState(false);
  const [reason, setReason] = useState('');
  const [receivedGoods, setReceivedGoods] = useState<ReceivedGoods>('KEEP_IN_STOCK');
  const [reasonKey, setReasonKey] = useState('');
  const [note, setNote] = useState('');
  const [goodsChoices, setGoodsChoices] = useState<Partial<Record<number, CancelGoodsChoice>>>({});
  const dialogRef = useModalDialog(() => settle(null));
  const reasonId = useId();
  const noteId = useId();
  // A preset reason stands in for the pick: the type is the one CS chose and the lock reason, if CS gave one, is CS's
  // too. A locked order with no lock reason on the preset (locked after CS filed, or CS's did not fit) still asks it.
  const asking = askCancelType && preset === undefined;
  const picked = asking ? CANCEL_REASONS.get(reasonKey) : undefined;
  const cancelType = preset?.cancelType ?? picked?.type ?? null;
  const locked = askCancelType && lock !== null && !preset?.lockOverrideReason;
  // The picked reason leads the event's reason and the note follows it, so the note gets what the longest reason leaves.
  const memoMax = Math.max(0, maxReasonLength - LONGEST_REASON - MEMO_JOIN.length);
  // One Note box (SSD-7676): the memo after the reason, and on a locked order also why it is cancelled after the lock.
  const noteRequired = locked || picked?.needsMemo === true;
  const noteBlank = note.trim() === '';
  const goodsAnswered = cancelGoodsAnswered(goodsLines, goodsChoices);
  const unticked = confirmCheck !== undefined && !checked;
  // The goods and the parcel box come after the reason, in the order the form shows them.
  const goodsWhy = !goodsAnswered
    ? unticked
      ? 'Tick the parcel box'
      : "Choose each line's goods"
    : unticked
      ? 'Tick the parcel box'
      : null;
  const noteWhy =
    noteRequired && noteBlank
      ? locked
        ? 'Write why it is cancelled after the lock'
        : 'Write what the reason was'
      : null;
  /** What still holds the confirm, said beside it so a grey button is never a puzzle; null once it can go. */
  const holdingWhy = preset
    ? (noteWhy ?? goodsWhy)
    : askCancelType
      ? picked === undefined
        ? 'Pick a reason'
        : (noteWhy ?? goodsWhy)
      : reason.trim() === ''
        ? 'Write a reason'
        : null;
  const blocked = holdingWhy !== null;
  const submit = () => {
    if (blocked) {
      return;
    }
    const written = note.trim();
    // On a locked order the note is the lock reason the server posts; the reason keeps it only when the reason needs it.
    const memoPart = locked && picked?.needsMemo !== true ? '' : written.slice(0, memoMax).trim();
    const answer: CancelAnswer = {
      reason: preset
        ? preset.reason
        : picked
          ? [picked.label, memoPart].filter((part) => part !== '').join(MEMO_JOIN)
          : reason.trim(),
    };
    if (showReceivedGoods) {
      answer.receivedGoods = receivedGoods;
    }
    if (cancelType !== null) {
      answer.cancelType = cancelType;
      if (locked) {
        answer.lockOverrideReason = written;
      } else if (preset?.lockOverrideReason) {
        answer.lockOverrideReason = preset.lockOverrideReason;
      }
      if (goodsLines.length > 0) {
        answer.lines = cancelGoodsDispositions(goodsLines, goodsChoices);
      }
      const goodsNote = describeCancelGoods(goodsLines, goodsChoices);
      if (goodsNote !== '') {
        answer.goodsNote = goodsNote;
      }
      if (goodsLines.some((line) => line.kind === 'BOUGHT')) {
        answer.goodsChoices = goodsChoices;
      }
    }
    settle(answer);
  };

  return (
    <dialog className="wms-prompt cancel-prompt" ref={dialogRef} aria-label={title}>
      <header>
        {title}
        <button type="button" className="x" aria-label="Close" onClick={() => settle(null)}>
          ✕
        </button>
      </header>
      <div className="body">
        {(notice || (askCancelType && lock && (asking || locked))) && (
          <div className="cancel-chips">
            {notice && (
              <span className="cancel-chip is-amber" data-testid="cancel-dialog-notice">
                {notice}
              </span>
            )}
            {askCancelType && lock && (asking || locked) && (
              <span className="cancel-chip" data-testid="cancel-dialog-lock">
                <LockIcon />
                {describeCancelLock(lock)}
              </span>
            )}
          </div>
        )}
        {preset && (
          <div className="qrow cancel-preset" data-testid="cancel-dialog-preset">
            <b>Requested{preset.requestedBy ? ` by ${preset.requestedBy}` : ''}</b>
            <span>{preset.reason}</span>
          </div>
        )}
        {asking && (
          <>
            <div className="qrow cancel-reason">
              <label htmlFor={reasonId}>
                <b>Cancel reason</b>
              </label>
              <select id={reasonId} value={reasonKey} onChange={(event) => setReasonKey(event.target.value)}>
                <option value="">Select a reason</option>
                {CANCEL_REASON_GROUPS.map((group, g) => (
                  <optgroup key={group.type} label={group.label}>
                    {group.reasons.map((choice, r) => (
                      <option key={choice.label} value={`${g}-${r}`}>
                        {choice.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </>
        )}
        {preset && !locked ? null : askCancelType ? (
          <div className="cancel-note">
            <div className="cancel-note-head">
              <label htmlFor={noteId}>
                <b>Note</b>
              </label>
              <span className={`cancel-note-tag${noteRequired ? ' is-required' : ''}`}>
                {noteRequired ? 'Required' : 'Optional'}
              </span>
            </div>
            <textarea
              id={noteId}
              aria-label="Note"
              rows={2}
              value={note}
              maxLength={locked ? OVERRIDE_MAX : memoMax}
              aria-required={noteRequired}
              placeholder={
                locked
                  ? 'Why it is cancelled after the lock, e.g. CS confirmed it with the customer'
                  : picked?.needsMemo
                    ? 'What the reason was'
                    : 'Details, if any'
              }
              onChange={(event) => setNote(event.target.value)}
            />
            <p className="cancel-note-help">Goes to the order with the cancel.</p>
          </div>
        ) : (
          <div className="qrow">
            <b>Reason</b>
            <input
              type="text"
              aria-label="Reason"
              value={reason}
              maxLength={maxReasonLength}
              onChange={(event) => setReason(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  submit();
                }
              }}
            />
          </div>
        )}
        {/* The parcel first, then what each line's goods go back to, since the goods are in that parcel (SSD-7676). */}
        {confirmCheck && (
          <label className="chk cancel-confirm-check">
            <input
              type="checkbox"
              aria-label={confirmCheck}
              checked={checked}
              onChange={(event) => setChecked(event.target.checked)}
            />
            <span>
              {confirmCheck}
              {confirmHint && <small className="cancel-confirm-hint">{confirmHint}</small>}
            </span>
          </label>
        )}
        {/* Each line's goods: said for existing inventory, asked for a bought line, before the cancel can go (SSD-7643). */}
        {askCancelType && goodsLines.length > 0 && (
          <fieldset className="cancel-goods" data-testid="cancel-goods">
            <legend>What to do with the goods</legend>
            <ul>
              {goodsLines.map((line) => (
                <li key={line.lineNo} className="cancel-goods-row">
                  <span className="cancel-goods-item" title={`${line.sku} ${line.name}`}>
                    <b>{line.lineNo}</b> {line.sku} ×{line.quantity}
                    {line.name && <span className="name">{line.name}</span>}
                  </span>
                  {line.kind === 'EXISTING' && (
                    <span className="cancel-goods-fixed">
                      <b>Existing Inventory</b> · back to stock
                    </span>
                  )}
                  {line.kind === 'NOT_BOUGHT' && (
                    <span className="cancel-goods-none">Not purchased - nothing to do</span>
                  )}
                  {line.kind === 'BOUGHT' && (
                    <span
                      className={`cancel-goods-choice${line.notShippable ? ' is-three' : ''}`}
                      role="radiogroup"
                      aria-label={`Line ${line.lineNo} goods`}
                    >
                      {/* [SSD-7906] A bought line that never arrived can also be one the seller never shipped. */}
                      {(line.notShippable ? GOODS_CHOICES_UNARRIVED : GOODS_CHOICES).map((choice) => (
                        <label key={choice} className={goodsChoices[line.lineNo] === choice ? 'is-picked' : undefined}>
                          <input
                            type="radio"
                            name={`cancel-goods-${line.lineNo}`}
                            value={choice}
                            checked={goodsChoices[line.lineNo] === choice}
                            onChange={() => setGoodsChoices((previous) => ({ ...previous, [line.lineNo]: choice }))}
                          />
                          {choice === 'RETURN'
                            ? 'Return to seller'
                            : choice === 'NOT_SHIPPED'
                              ? 'Not shipped by seller'
                              : line.arrived
                                ? 'Keep in stock'
                                : 'Keep when it arrives'}
                        </label>
                      ))}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </fieldset>
        )}
        {showReceivedGoods && (
          <fieldset>
            <legend>Received goods</legend>
            <p>
              What should happen to received goods that can be written off? Goods filled from existing inventory stay in
              stock.
            </p>
            <label className="d-block">
              <input
                type="radio"
                name="receivedGoods"
                value="KEEP_IN_STOCK"
                checked={receivedGoods === 'KEEP_IN_STOCK'}
                onChange={() => setReceivedGoods('KEEP_IN_STOCK')}
              />{' '}
              Keep in stock — release the reservation; stock quantity stays unchanged.
            </label>
            <label className="d-block">
              <input
                type="radio"
                name="receivedGoods"
                value="WRITE_OFF"
                checked={receivedGoods === 'WRITE_OFF'}
                onChange={() => setReceivedGoods('WRITE_OFF')}
              />{' '}
              Write off — remove eligible received goods from stock.
            </label>
          </fieldset>
        )}
      </div>
      <div className="foot cancel-foot">
        <div className="cancel-actions">
          <span className="cancel-holding" data-testid="cancel-holding">
            {holdingWhy ?? ''}
          </span>
          <button type="button" className="btn btn-line btn-sm" onClick={() => settle(null)}>
            Keep order
          </button>
          <button type="button" className="btn btn-red-line btn-sm" disabled={blocked} onClick={submit}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
};

/**
 * The cancel dialog that asks more than a reason: the stock choice for received goods that can be written off, and
 * for an order cancel whose reason it is (SSD-7450). The ordinary one-line prompts elsewhere stay unchanged.
 */
export const useReceivedGoodsCancelDialog = () => {
  const [pending, setPending] = useState<Pending | null>(null);
  const nextId = useRef(0);
  const resolvePending = useRef<((answer: CancelAnswer | null) => void) | null>(null);
  useEffect(
    () => () => {
      resolvePending.current?.(null);
    },
    [],
  );
  const askReceivedGoods = (request: CancelDialogRequest) =>
    new Promise<CancelAnswer | null>((resolve) => {
      // Keep state updaters pure: resolving the previous caller belongs outside React's updater.
      resolvePending.current?.(null);
      resolvePending.current = resolve;
      nextId.current += 1;
      setPending({
        ...request,
        id: nextId.current,
        settle: (answer) => {
          if (resolvePending.current !== resolve) {
            return;
          }
          resolvePending.current = null;
          setPending(null);
          resolve(answer);
        },
      });
    });
  /** Closes an open question as backed out, for a page that can no longer stand behind what it asked about. */
  const dismiss = useCallback(() => {
    resolvePending.current?.(null);
    resolvePending.current = null;
    setPending(null);
  }, []);
  return { askReceivedGoods, dismiss, dialog: pending ? <CancelModal key={pending.id} {...pending} /> : null };
};
